import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { jwtDecode } from 'jwt-decode';

declare const google: any;

export interface GoogleUser {
  sub: string; // Google Subject ID
  email: string;
  name: string;
  picture: string;
  given_name: string;
  family_name: string;
}

export interface AuthResponse {
  success: boolean;
  sessionToken: string;
  refreshToken?: string;
  user: {
    id: number;
    email: string;
    name: string;
    picture: string;
    role: string;
    google_sub: string;
  };
}

export interface SessionInfo {
  user: GoogleUser;
  role: string;
  sessionToken: string;
  expiresAt: number;
}

@Injectable({
  providedIn: 'root'
})
export class GoogleAuthService {
  private readonly GOOGLE_CLIENT_ID = environment.googleClientId;
  private readonly SESSION_STORAGE_KEY = 'userSession';
  private readonly REFRESH_TOKEN_KEY = 'refreshToken';

  // Frontend gate: only these emails may sign in. Backend enforcement is a follow-up.
  private readonly ALLOWED_EMAILS: readonly string[] = ['admin@intellipreptech.com'];

  private rejectionSubject = new BehaviorSubject<string | null>(null);
  public rejection$ = this.rejectionSubject.asObservable();

  private isEmailAllowed(email: string | undefined | null): boolean {
    if (!email) return false;
    return this.ALLOWED_EMAILS.includes(email.trim().toLowerCase());
  }
  
  private authStateSubject = new BehaviorSubject<boolean>(false);
  private userSubject = new BehaviorSubject<GoogleUser | null>(null);
  
  public authState$ = this.authStateSubject.asObservable();
  public user$ = this.userSubject.asObservable();
  
  private refreshTokenTimer?: number;
  
  constructor(
    private http: HttpClient,
    private router: Router
  ) {
    this.initializeAuthState();
  }

  /**
   * Initialize authentication state from stored session
   */
  private initializeAuthState(): void {
    const sessionInfo = this.getStoredSession();
    if (sessionInfo && this.isSessionValid(sessionInfo)) {
      this.authStateSubject.next(true);
      this.userSubject.next(sessionInfo.user);
      this.scheduleTokenRefresh(sessionInfo.expiresAt);
    } else {
      this.clearStoredSession();
    }
  }

  /**
   * Load and initialize Google Identity Services
   */
  async loadGoogleIdentityServices(): Promise<void> {
    console.log('📚 GoogleAuthService: loadGoogleIdentityServices called');
    
    return new Promise((resolve, reject) => {
      if (typeof google !== 'undefined' && google.accounts) {
        console.log('✅ GoogleAuthService: Google Identity Services already loaded');
        resolve();
        return;
      }

      console.log('📥 GoogleAuthService: Loading Google Identity Services script...');
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        console.log('✅ GoogleAuthService: Google Identity Services script loaded successfully');
        resolve();
      };
      script.onerror = () => {
        console.error('❌ GoogleAuthService: Failed to load Google Identity Services script');
        reject(new Error('Failed to load Google Identity Services'));
      };
      document.head.appendChild(script);
    });
  }

  /**
   * Initialize Google Sign-In
   */
  async initializeGoogleSignIn(): Promise<void> {
    console.log('🔧 GoogleAuthService: initializeGoogleSignIn called');
    
    await this.loadGoogleIdentityServices();
    
    console.log('⚙️ GoogleAuthService: Initializing Google accounts with client ID:', this.GOOGLE_CLIENT_ID);
    
    google.accounts.id.initialize({
      client_id: this.GOOGLE_CLIENT_ID,
      callback: (response: any) => {
        console.log('🎯 GoogleAuthService: Google callback triggered');
        this.handleCredentialResponse(response);
      },
      ux_mode: 'popup',
      use_fedcm_for_prompt: true // Use FedCM as per design document
    });
    
    console.log('✅ GoogleAuthService: Google Sign-In initialization completed');
  }

  /**
   * Render Google Sign-In button
   */
  renderSignInButton(containerId: string, options?: any): void {
    console.log('🎨 GoogleAuthService: renderSignInButton called with containerId:', containerId);
    console.log('🎨 GoogleAuthService: Button options:', options);
    
    const defaultOptions = {
      theme: 'outline',
      size: 'large',
      text: 'signin_with',
      logo_alignment: 'left'
    };

    const buttonOptions = { ...defaultOptions, ...options };
    console.log('🎨 GoogleAuthService: Final button options:', buttonOptions);
    
    const element = document.getElementById(containerId);
    if (!element) {
      console.error('❌ GoogleAuthService: Element not found with ID:', containerId);
      return;
    }
    
    console.log('✅ GoogleAuthService: Element found, rendering button...');
    
    try {
      google.accounts.id.renderButton(element, buttonOptions);
      console.log('✅ GoogleAuthService: Button rendered successfully');
    } catch (error) {
      console.error('❌ GoogleAuthService: Error rendering button:', error);
    }
  }

  /**
   * Handle the credential response from Google Identity Services
   */
  private async handleCredentialResponse(response: any): Promise<void> {
    // Decode the Google-signed ID token first so we can gate on email before any session is issued.
    let decoded: any;
    try {
      decoded = jwtDecode(response.credential);
    } catch (decodeError) {
      console.error('❌ Google Auth: Failed to decode Google ID token', decodeError);
      this.rejectionSubject.next('Sign-in failed: could not parse Google credential.');
      return;
    }

    if (!this.isEmailAllowed(decoded?.email)) {
      const msg = `Access denied: ${decoded?.email || 'this account'} is not authorized. Only admin@intellipreptech.com may sign in.`;
      console.warn('🚫 Google Auth:', msg);
      this.rejectionSubject.next(msg);
      this.clearStoredSession();
      this.clearStoredRefreshToken();
      this.authStateSubject.next(false);
      this.userSubject.next(null);
      if (typeof google !== 'undefined' && google.accounts) {
        google.accounts.id.disableAutoSelect();
      }
      return;
    }

    this.rejectionSubject.next(null);

    try {
      console.log('🔐 Google Auth: Received credential response for allowed email');
      const credential = response.credential;

      console.log('🔗 Google Auth: About to call backend verification endpoint:', `${environment.apiBaseUrl}/auth/google`);

      // Send the ID token to backend for verification
      const authResponse = await this.verifyTokenWithBackend(credential).toPromise();

      console.log('✅ Google Auth: Backend verification successful', authResponse);

      if (authResponse && authResponse.success) {
        const sessionInfo: SessionInfo = {
          user: {
            sub: authResponse.user.google_sub,
            email: authResponse.user.email,
            name: authResponse.user.name,
            picture: authResponse.user.picture,
            given_name: authResponse.user.name.split(' ')[0],
            family_name: authResponse.user.name.split(' ').slice(1).join(' ')
          },
          role: authResponse.user.role,
          sessionToken: authResponse.sessionToken,
          expiresAt: Date.now() + (15 * 60 * 1000) // 15 minutes from now
        };

        this.storeSession(sessionInfo);
        this.storeRefreshToken(authResponse.refreshToken);

        this.authStateSubject.next(true);
        this.userSubject.next(sessionInfo.user);

        this.scheduleTokenRefresh(sessionInfo.expiresAt);

        console.log('🎉 Google Auth: Authentication completed successfully');
        this.router.navigate(['/home']);
        return;
      }
    } catch (error: any) {
      console.error('❌ Google Auth: Backend verification failed, falling back to client-side session for the allowed admin', error);
    }

    // Backend verification failed. Allowed admin email already passed the Google-signed gate above,
    // so issue a client-only session for UI/UX work. TODO: enforce on backend.
    const fallbackSession: SessionInfo = {
      user: {
        sub: decoded.sub,
        email: decoded.email,
        name: decoded.name,
        picture: decoded.picture,
        given_name: decoded.given_name,
        family_name: decoded.family_name
      },
      role: 'admin',
      sessionToken: 'client-only-session-' + Date.now(),
      expiresAt: Date.now() + (15 * 60 * 1000)
    };

    this.storeSession(fallbackSession);
    this.authStateSubject.next(true);
    this.userSubject.next(fallbackSession.user);
    console.log('🧪 Google Auth: Client-only session issued for allowed admin (backend offline)');
    this.router.navigate(['/home']);
  }

  /**
   * Verify ID token with backend
   */
  private verifyTokenWithBackend(idToken: string): Observable<AuthResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.http.post<AuthResponse>(
      `${environment.apiBaseUrl}/auth/google`,
      { id_token: idToken },
      { headers }
    ).pipe(
      catchError(error => {
        console.error('Backend verification failed:', error);
        return throwError(error);
      })
    );
  }

  /**
   * Get current session token for API calls
   */
  getSessionToken(): string | null {
    const sessionInfo = this.getStoredSession();
    return sessionInfo && this.isSessionValid(sessionInfo) ? sessionInfo.sessionToken : null;
  }

  /**
   * Get HTTP headers with authorization
   */
  getAuthHeaders(): HttpHeaders {
    const token = this.getSessionToken();
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : ''
    });
  }

  /**
   * Check if user is currently logged in
   */
  isLoggedIn(): boolean {
    const sessionInfo = this.getStoredSession();
    return sessionInfo !== null && this.isSessionValid(sessionInfo);
  }

  /**
   * Get current user information
   */
  getCurrentUser(): GoogleUser | null {
    const sessionInfo = this.getStoredSession();
    return sessionInfo && this.isSessionValid(sessionInfo) ? sessionInfo.user : null;
  }

  /**
   * Get current user role
   */
  getCurrentUserRole(): string | null {
    const sessionInfo = this.getStoredSession();
    return sessionInfo && this.isSessionValid(sessionInfo) ? sessionInfo.role : null;
  }

  /**
   * Whether the current user is the platform admin.
   * During the UI/UX refresh phase only this email may sign in, so
   * `isAdmin === isLoggedIn` today; this helper exists for the public
   * launch where any Google user can sign in but admin tools (layout
   * switcher, settings) remain restricted.
   */
  isAdmin(): boolean {
    const user = this.getCurrentUser();
    return user?.email?.toLowerCase() === 'admin@intellipreptech.com';
  }

  /**
   * Refresh session token
   */
  private async refreshSessionToken(): Promise<boolean> {
    try {
      const refreshToken = this.getStoredRefreshToken();
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const headers = new HttpHeaders({
        'Content-Type': 'application/json'
      });

      const response = await this.http.post<AuthResponse>(
        `${environment.apiBaseUrl}/auth/refresh`,
        { refresh_token: refreshToken },
        { headers }
      ).toPromise();

      if (response && response.success) {
        const currentSession = this.getStoredSession();
        if (currentSession) {
          const updatedSession: SessionInfo = {
            ...currentSession,
            sessionToken: response.sessionToken,
            expiresAt: Date.now() + (15 * 60 * 1000)
          };
          
          this.storeSession(updatedSession);
          this.scheduleTokenRefresh(updatedSession.expiresAt);
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Token refresh failed:', error);
      this.logout();
      return false;
    }
  }

  /**
   * Schedule automatic token refresh
   */
  private scheduleTokenRefresh(expiresAt: number): void {
    if (this.refreshTokenTimer) {
      clearTimeout(this.refreshTokenTimer);
    }

    // Refresh 2 minutes before expiry
    const refreshTime = expiresAt - Date.now() - (2 * 60 * 1000);
    
    if (refreshTime > 0) {
      this.refreshTokenTimer = window.setTimeout(() => {
        this.refreshSessionToken();
      }, refreshTime);
    }
  }

  /**
   * Logout user
   */
  logout(): void {
    this.clearStoredSession();
    this.clearStoredRefreshToken();
    
    if (this.refreshTokenTimer) {
      clearTimeout(this.refreshTokenTimer);
      this.refreshTokenTimer = undefined;
    }

    this.authStateSubject.next(false);
    this.userSubject.next(null);
    
    // Optionally revoke Google session
    if (typeof google !== 'undefined' && google.accounts) {
      google.accounts.id.disableAutoSelect();
    }
    
    this.router.navigate(['/']);
  }

  /**
   * Store session information
   */
  private storeSession(sessionInfo: SessionInfo): void {
    sessionStorage.setItem(this.SESSION_STORAGE_KEY, JSON.stringify(sessionInfo));
  }

  /**
   * Get stored session information
   */
  private getStoredSession(): SessionInfo | null {
    const stored = sessionStorage.getItem(this.SESSION_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  }

  /**
   * Clear stored session
   */
  private clearStoredSession(): void {
    sessionStorage.removeItem(this.SESSION_STORAGE_KEY);
  }

  /**
   * Store refresh token (HTTP-only cookie would be better, but using localStorage for demo)
   */
  private storeRefreshToken(refreshToken: string | undefined): void {
    if (refreshToken) {
      localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
    }
  }

  /**
   * Get stored refresh token
   */
  private getStoredRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  /**
   * Clear stored refresh token
   */
  private clearStoredRefreshToken(): void {
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
  }

  /**
   * Check if session is still valid
   */
  private isSessionValid(sessionInfo: SessionInfo): boolean {
    return Date.now() < sessionInfo.expiresAt;
  }

  /**
   * Manual sign-in trigger (for cases where button render doesn't work)
   */
  async signIn(): Promise<void> {
    await this.initializeGoogleSignIn();
    google.accounts.id.prompt();
  }
} 